import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../services/api';
import './Login.css';

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallAndroid = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstalled(true);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await login(formData.email, formData.password);
      // Guardar token en localStorage para clientes nativos (Capacitor/iOS) que no pueden usar cookies cross-origin
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Credenciales incorrectas. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      {/* Left panel */}
      <div className="login-panel-left" aria-hidden="true">
        <div className="login-panel-brand">
          <div className="login-panel-logo">
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <path d="M16 4L4 12V28H12V20H20V28H28V12L16 4Z" fill="white"/>
            </svg>
          </div>
          <span className="login-panel-brand-name">HabitaPro</span>
        </div>
        <div className="login-panel-content">
          <h2 className="login-panel-headline">Tu comunidad,<br/>conectada<span className="login-panel-dot">.</span></h2>
          <p className="login-panel-sub">Gestiona incidencias, reservas, documentos y comunicaciones desde un solo lugar.</p>
          <ul className="login-panel-features">
            <li>
              <span className="login-feature-icon">⚡</span>
              <span>Incidencias resueltas hasta un 60% más rápido</span>
            </li>
            <li>
              <span className="login-feature-icon">📅</span>
              <span>Reserva zonas comunes en segundos</span>
            </li>
            <li>
              <span className="login-feature-icon">🔔</span>
              <span>Notificaciones en tiempo real para todos</span>
            </li>
            <li>
              <span className="login-feature-icon">🔒</span>
              <span>Datos seguros y privacidad garantizada</span>
            </li>
          </ul>
        </div>
        <div className="login-panel-footer">
          <div className="login-panel-avatars">
            <div className="login-avatar" style={{background:'#818CF8'}}>A</div>
            <div className="login-avatar" style={{background:'#34D399'}}>M</div>
            <div className="login-avatar" style={{background:'#FB923C'}}>R</div>
            <div className="login-avatar" style={{background:'#F472B6'}}>S</div>
          </div>
          <p className="login-panel-social-proof">Gestión digital para comunidades de vecinos</p>
        </div>
      </div>

      {/* Right panel – form */}
      <div className="login-panel-right">
        <div className="login-form-wrap">
          <div className="login-form-header">
            <div className="login-form-logo-sm">
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                <path d="M16 4L4 12V28H12V20H20V28H28V12L16 4Z" fill="white"/>
              </svg>
            </div>
            <h1 className="login-form-title">Bienvenido de nuevo</h1>
            <p className="login-form-sub">Accede a tu comunidad</p>
          </div>

          {error && (
            <div className="login-error-box" role="alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label className="login-label" htmlFor="login-email">Correo electrónico</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                </span>
                <input
                  id="login-email"
                  type="email"
                  name="email"
                  className="login-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="tu@email.com"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="login-field">
              <div className="login-label-row">
                <label className="login-label" htmlFor="login-password">Contraseña</label>
                <Link to="/forgot-password" className="login-forgot">¿La olvidaste?</Link>
              </div>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  id="login-password"
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  className="login-input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-toggle-pass"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPass ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-submit"
              disabled={loading}
            >
              {loading ? (
                <span className="login-spinner" />
              ) : (
                <>
                  Iniciar sesión
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <p className="login-register-link">
            ¿Aún no tienes cuenta?{' '}
            <Link to="/register">Regístrate gratis</Link>
          </p>

          <div className="login-app-download">
            <div className="login-app-divider">
              <span>Descarga la app</span>
            </div>
            <div className="login-app-buttons">
              <a
                href="https://apps.apple.com/us/app/habitapro/id6761760827"
                className="login-store-btn login-store-btn--ios"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Descargar en App Store"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="login-store-btn-text">
                  <span className="login-store-btn-sub">Disponible en</span>
                  <span className="login-store-btn-main">App Store</span>
                </div>
              </a>
              <button
                onClick={handleInstallAndroid}
                className={`login-store-btn login-store-btn--android${isInstalled ? ' login-store-btn--installed' : ''}${!installPrompt && !isInstalled ? ' login-store-btn--unavailable' : ''}`}
                disabled={isInstalled || (!installPrompt && !isInstalled)}
                aria-label="Instalar app en Android"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.523 15.341l-.001-.001c-.34.593-.714 1.169-1.129 1.703a18.57 18.57 0 0 1-.662.815 3.41 3.41 0 0 1-2.3 1.13 3.11 3.11 0 0 1-1.12-.214l-.003-.001a3.98 3.98 0 0 0-1.427-.263c-.49 0-.975.09-1.43.265l-.003.001a3.11 3.11 0 0 1-1.115.212 3.41 3.41 0 0 1-2.304-1.127 18.34 18.34 0 0 1-.666-.82 18.18 18.18 0 0 1-1.128-1.7C4.617 13.656 4 11.854 4 10.103c0-1.7.427-3.094 1.27-4.148a4.784 4.784 0 0 1 3.846-1.94c.533 0 1.11.115 1.713.34.585.222 1.014.38 1.28.471.28.094.54.141.779.141.265 0 .543-.053.832-.16.095-.034.526-.196 1.249-.427a4.877 4.877 0 0 1 1.59-.262c.93 0 1.772.25 2.499.743a4.7 4.7 0 0 1 1.003.905 4.633 4.633 0 0 0-.601.447 4.415 4.415 0 0 0-1.486 3.33c.003 1.658.817 3.005 2.082 3.727l-.533.911zM14.49 1.87A4.42 4.42 0 0 1 13.403 4.8a3.826 3.826 0 0 1-3.084 1.52 3.483 3.483 0 0 1-.025-.38c0-1.047.383-2.166 1.063-3.015A4.502 4.502 0 0 1 12.8 1.68a4.197 4.197 0 0 1 1.655-.68c.013.29.02.58.035.87z"/>
                </svg>
                <div className="login-store-btn-text">
                  <span className="login-store-btn-sub">
                    {isInstalled ? 'Ya instalada' : 'Instalar en'}
                  </span>
                  <span className="login-store-btn-main">Android</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
